import { Tooltip, Typography } from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { makeStyles } from "@mui/styles";
import tokens from "../../styles/designTokens";
import { SuggestionStatusEnum } from "../../redux/suggestions";
import { SuggestionIconType } from "../../models/suggestionStatus.interface";

const useStyles = makeStyles(() => ({
  suggestionIconContainer: {
    width: '2.25rem',
    height: '2.25rem',
    borderRadius: '0.25rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  suggestionIconContainerExpanded: {
    width: '100%',
    height: '2.25rem',
    borderRadius: '0.25rem',
    display: 'flex',
    border: `1px solid ${tokens.neutral90}`,
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 500,
    padding: '0 0.5rem',
  },
  suggestionIcon: {
    cursor: 'pointer',
    fontSize: '1.5rem',
  },
  suggestionIconExpanded: {
    cursor: 'pointer',
    paddingRight: '0.25rem',
    fontSize: '1.25rem',
  },
  suggestionToReviewContainer: {
    backgroundColor: tokens.neutral80,
  },
  suggestionToReviewContainerExpanded: {
    backgroundColor: tokens.white,
    color: tokens.core1,
  },
  suggestionToReviewIcon: {
    fill: tokens.core2,
  },
  suggestionToReviewIconExpanded: {
    fill: tokens.core1,
  },
  suggestionCompletedContainer: {
    backgroundColor: tokens.positive15,
    color: tokens.positive100,
    borderColor: tokens.positive15,
  },
  suggestionCompletedIcon: {
    fill: tokens.positive70,
  },
  suggestionCompletedIconExpanded: {
    fill: tokens.positive100,
  },
  suggestionIgnoredContainer: {
    backgroundColor: tokens.error15,
    color: tokens.error100,
    borderColor: tokens.error15,
  },
  suggestionIgnoredIcon: {
    fill: tokens.error100,
  },
  suggestionButtonExpandedText: {
    fontSize: '0.875rem'
  },
}));

export const SuggestionStatusButton = ({
  suggestionId,
  status,
  iconType,
  isExpanded,
  onStatusChange
}: {
  suggestionId: string;
  status: SuggestionStatusEnum;
  isExpanded: boolean;
  iconType: SuggestionIconType;
  onStatusChange: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string, status: SuggestionIconType) => void;
}) => {
  const classes = useStyles();

  const getSuggestionButtonData = () => {
    const iconMap = {
      completed: {
        [SuggestionStatusEnum.ToReview]: CheckCircleOutlinedIcon,
        [SuggestionStatusEnum.Completed]: CheckCircleIcon,
        [SuggestionStatusEnum.Ignored]: CheckCircleOutlinedIcon
      },
      ignored: {
        [SuggestionStatusEnum.ToReview]: CancelOutlinedIcon,
        [SuggestionStatusEnum.Completed]: CancelOutlinedIcon,
        [SuggestionStatusEnum.Ignored]: CancelIcon
      }
    };

    let text = null;
    if (isExpanded) {
      if (iconType === SuggestionIconType.Completed) {
        text = 'Complete';
      } else {
        text = 'Ignore';
      }
    }

    const getToReviewClasses = (expanded: boolean) => ({
      containerClass: expanded ? classes.suggestionToReviewContainerExpanded : classes.suggestionToReviewContainer,
      iconClass: expanded ? classes.suggestionToReviewIconExpanded : classes.suggestionToReviewIcon
    });
    const getCompletedClasses = (expanded: boolean) => ({
      containerClass: classes.suggestionCompletedContainer,
      iconClass: expanded ? classes.suggestionCompletedIconExpanded : classes.suggestionCompletedIcon
    });
    const getIgnoredClasses = (expanded: boolean) => ({
      containerClass: expanded ? classes.suggestionIgnoredContainer : classes.suggestionIgnoredContainer,
      iconClass: classes.suggestionIgnoredIcon
    });

    const getClasses = (iconType: SuggestionIconType, isExpanded: boolean, status: SuggestionStatusEnum) => {
      if (iconType === SuggestionIconType.Completed) {
        if (status === SuggestionStatusEnum.Completed) {
          return getCompletedClasses(isExpanded);
        }
        // toReview and ignored use the same classes, just switch expanded
        return getToReviewClasses(isExpanded);
      } else {
        if (status === SuggestionStatusEnum.Ignored) {
          return getIgnoredClasses(isExpanded);
        }
        // toReview and completed use the same classes, just switch expanded
        return getToReviewClasses(isExpanded);
      }
    };

    const getTooltipTitle = (iconType: SuggestionIconType, status: SuggestionStatusEnum) => {
      if (iconType.toString() === status.toString()) {
        return `Revert to review`;
      }
      if (iconType === SuggestionIconType.Completed) {
        return 'Mark as completed';
      }
      return 'Mark as ignored';
    };

    const IconComponent = iconMap[iconType][status];
    const { containerClass, iconClass } = getClasses(iconType, isExpanded, status);
    const tooltipTitle = getTooltipTitle(iconType, status);
    return { containerClass, iconClass, IconComponent, text, tooltipTitle };
  };

  const { containerClass, iconClass, IconComponent, text, tooltipTitle } = getSuggestionButtonData();
  const baseContainerClass = isExpanded ? classes.suggestionIconContainerExpanded : classes.suggestionIconContainer;
  const baseIconClass = isExpanded ? classes.suggestionIconExpanded : classes.suggestionIcon;

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top-end"
    >
      <div
        onClick={(e) => onStatusChange(e, suggestionId, iconType)}
        className={`${baseContainerClass} ${containerClass}`}
        style={{ cursor: 'pointer' }}
      >
        <IconComponent
          className={`${baseIconClass} ${iconClass}`}
        />
        {text && (
          <Typography className={classes.suggestionButtonExpandedText} variant="body1">{text}</Typography>
        )}
      </div>
    </Tooltip>
  );
};